/**
 * Tenant Show — Read-only detail view with tabbed layout, status actions, and activity log.
 * @see Documentation/Specification/020_Definitions/application-agnostic/identity/Tenant.md
 */
import { useGetOne, useNavigate } from 'react-admin';
import {
  Box, Typography, Paper, Grid, Chip, Avatar, Button, CircularProgress, Divider,
} from '@mui/material';
import { ArrowBack, Edit as EditIcon } from '@mui/icons-material';
import { Tab, TabbedShowLayout } from 'react-admin';
import { TenantRecord, STATUS_COLORS } from './types';
import { TenantStatusActions } from './TenantStatusActions';
import { TenantActivityLog } from './TenantActivityLog';
import { TenantSettingsPanel } from './TenantSettingsPanel';
import { TenantUsersPanel } from './TenantUsersPanel';

const useTenantId = () => window.location.pathname.split('/tenants/')[1]?.split('/')[0] ?? '';

export const TenantShow = () => {
  const navigate = useNavigate();
  const id = useTenantId();
  const { data: tenant, isLoading } = useGetOne('tenants', { id });

  if (isLoading || !tenant) {
    return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  }

  const t = tenant as unknown as TenantRecord;

  return (
    <Box sx={{ p: 2 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/tenants')} sx={{ mb: 2 }}>
        Back to Tenants
      </Button>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontSize: 20 }}>
            {t.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={600}>{t.name}</Typography>
            <Typography variant="body2" color="text.secondary">{t.slug} · {t.tenantId}</Typography>
          </Box>
          <Chip label={t.status} color={STATUS_COLORS[t.status] ?? 'default'} />
        </Box>
        {t.registeredName && (
          <Typography variant="body2" color="text.secondary">Registered as: {t.registeredName}</Typography>
        )}
        {t.registrationNumber && (
          <Typography variant="body2" color="text.secondary">Reg #: {t.registrationNumber}</Typography>
        )}

        {/* Status Actions */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TenantStatusActions tenantId={t.id} status={t.status} />
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/tenants/${t.id}/edit`)} size="small">
            Edit
          </Button>
        </Box>
      </Paper>

      <TabbedShowLayout>
        <Tab label="Overview">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Contact Information</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                <Typography variant="body2">{t.contact?.email}</Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body2">{t.contact?.phone}</Typography>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Typography variant="caption" color="text.secondary">Address</Typography>
                <Typography variant="body2">{t.contact?.address || '—'}</Typography>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" fontWeight={600} mb={1}>Settings</Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 3 }}>
                <Typography variant="caption" color="text.secondary">Currency</Typography>
                <Typography variant="body2">{t.settings?.currency}</Typography>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="caption" color="text.secondary">Timezone</Typography>
                <Typography variant="body2">{t.settings?.timezone}</Typography>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="caption" color="text.secondary">Date Format</Typography>
                <Typography variant="body2">{t.settings?.dateFormat}</Typography>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="caption" color="text.secondary">Country</Typography>
                <Typography variant="body2">{t.settings?.country}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={600} mb={1}>Metadata</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">{t.metadata?.createdAt?.split('T')[0]} by {t.metadata?.createdBy}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Updated</Typography>
                <Typography variant="body2">{t.metadata?.updatedAt?.split('T')[0]} by {t.metadata?.updatedBy}</Typography>
              </Grid>
            </Grid>
          </Box>
        </Tab>
        <Tab label="Settings">
          <TenantSettingsPanel tenantId={t.id} />
        </Tab>
        <Tab label="Users">
          <TenantUsersPanel tenantId={t.id} />
        </Tab>
        <Tab label="Activity">
          <TenantActivityLog tenantId={t.tenantId ?? ''} />
        </Tab>
      </TabbedShowLayout>
    </Box>
  );
};
